import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";

export const PATIENT_FETCH_CONCURRENCY = 8;

export async function fetchOnePatient(
  patientRef: string
): Promise<[string, fhir5.Patient] | null> {
  const patientId = patientRef.split("/")[1] ?? patientRef;
  if (!patientId) return null;

  const url = `${endpoints.MPIFHIR}/Patient/${patientId}`;
  const headers = {
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    Accept: "application/fhir+json",
  };

  try {
    const response = await useAxios.get(url, { headers, timeout: 20000 });
    if (response.status === HTTPResponseStatus.Success && response.data) {
      return [patientId, response.data];
    }
  } catch (err) {
    console.error(`Erro ao buscar paciente ${patientId}:`, err);
  }
  return null;
}

export interface FetchPatientsParallelOptions {
  requestId: number;
  isRequestCurrent: (id: number) => boolean;
  onProgress: (loaded: number, total: number) => void;
  concurrency?: number;
}

export async function fetchPatientsParallel(
  patientRefs: string[],
  options: FetchPatientsParallelOptions
): Promise<Map<string, fhir5.Patient>> {
  const {
    requestId,
    isRequestCurrent,
    onProgress,
    concurrency = PATIENT_FETCH_CONCURRENCY,
  } = options;

  const uniqueRefs = Array.from(new Set(patientRefs));
  const total = uniqueRefs.length;
  onProgress(0, total);

  const patientMap = new Map<string, fhir5.Patient>();

  for (let i = 0; i < uniqueRefs.length; i += concurrency) {
    if (!isRequestCurrent(requestId)) return patientMap;

    const batch = uniqueRefs.slice(i, i + concurrency);
    const results = await Promise.allSettled(batch.map((ref) => fetchOnePatient(ref)));

    for (const result of results) {
      if (result.status === "fulfilled" && result.value) {
        const [id, patient] = result.value;
        patientMap.set(id, patient);
      }
    }

    const loaded = Math.min(i + batch.length, total);
    if (isRequestCurrent(requestId)) {
      onProgress(loaded, total);
    }
  }

  return patientMap;
}
